import { allModelsModule } from '../../models/models';
import { configServiceModule } from '../config-service/config-service';
import { jsDataModule } from '../../vendor/js-data';
import { ablyClientServiceModule } from '../ably-client-service/ably-client-service';
import { releaseAlertServiceModule } from '../release-alert-service/release-alert-service';
import { permissionsServiceModule } from '../permissions-service/permissions';

export const socketServiceModule = angular
  .module('Q.services.socket-service', [
    jsDataModule,
    allModelsModule,
    configServiceModule,
    ablyClientServiceModule,
    releaseAlertServiceModule,
    permissionsServiceModule,
  ])
  .factory(
    'socketService',
    function (
      DS,
      $rootScope,
      $window,
      $log,
      $q,
      $timeout,
      configService,
      ablyClientService,
      releaseAlertService,
      permissionsService,
    ) {
      const injectFlags = {};

      const whenReadyCallbacks = [];

      const svc = {
        client: null,
        channel: null,
        presenceWatchers: {},
        whenReady(cb) {
          return $q((resolve) => {
            if (svc.channel && svc.account) {
              resolve(cb(svc));
            } else {
              whenReadyCallbacks.push(() => resolve(cb(svc)));
            }
          });
        },
        // delays the creation of the socket and returns a promise instead, in order to allow the page time to
        // do other things
        create(account) {
          // if the channel was already created then just return it
          if (svc.account === account && svc.channel) {
            return svc.channel;
          } else {
            return _create(account, 'default');
          }
        },

        enterPresence(channelId, memberData) {
          svc.whenReady(() => _enterPresence(channelId, memberData));
        },

        leavePresence(channelId) {
          if (svc.client) {
            svc.client.channels.get(channelId).presence.leave();
          }
        },

        /**
         * Creates an object which will watch presence and manage a list that ensures a clientId (user id)
         * is only presence once within the list.
         * @param name watcher name, useful for retrieving the instance later using `socketService.presenceWatchers[name]`
         * @param channelId
         * @param onChange callback for when the underlying list is modified. Will be called on any list change, as well on initialization
         * @return {*}
         */
        createPresenceWatcher(name, channelId, onChange = angular.noop) {
          return svc.whenReady(() => {
            const channel = svc.client.channels.get(channelId);
            const watcher = (svc.presenceWatchers[name] = {
              channelId,
              channel,
              list: [],
            });
            const log = (member) =>
              $log.debug('Presence data received', member);
            const addOrUpdate = (member) => {
              log(member);
              // we only track unique client ids (we ignore multiple connections by the same client)
              // clientId is the candidateId
              const existing = watcher.list.find(
                (m) => m.clientId === member.clientId,
              );
              existing
                ? (existing.data = member.data)
                : watcher.list.push(member);
            };

            // get the initial list of current members
            channel.presence
              .get()
              .then((members) => {
                (members || []).forEach(addOrUpdate);
                onChange(watcher);
              })
              .catch((err) => {
                // 80002/80003: connection suspended/disconnected. A client
                // connectivity problem Ably recovers from, so don't alert on it
                const isConnectivityError =
                  err && (err.code === 80002 || err.code === 80003);
                $log[isConnectivityError ? 'warn' : 'error'](
                  '[Ably] presence.get failed',
                  err,
                );
              });
            channel.presence.subscribe('enter', (member) => {
              addOrUpdate(member);
              onChange(watcher);
            });
            channel.presence.subscribe('update', (member) => {
              addOrUpdate(member);
              onChange(watcher);
            });
            channel.presence.subscribe('leave', (member) => {
              log(member);
              // use connection id to minimize the chances of removing a client that has multiple connections only
              // NOTE: there is still the chance that the original connection is closed, causing them to be removed
              // when multiple connections are still live. For now we are going to allow this to happen in order to keep
              // things simple. The user will be reactivated on the new connection once the next update comes through.
              watcher.list = watcher.list.filter(
                (m) => m.connectionId !== member.connectionId,
              );
              onChange(watcher);
            });

            onChange(watcher);
            $log.debug(`Watching ${name} presence on ${channelId}`);
            return watcher;
          });
        },

        /**
         * Completely destroys an account's or user object's socket
         *
         * - Removes all listeners
         * - Disconnects the socket
         * - removes references to the socket
         */
        destroy() {
          $log.debug('Disconnecting sockets.');
          if (svc.client) {
            svc.client.connection.close();
          }
          svc.client = null;
          svc.channel = null;
        },
      };
      return svc;

      //// private functions

      function _enterPresence(channelId, memberData) {
        svc.client.channels.get(channelId).presence.enter(memberData);
      }

      /**
       * Creates a new socket based on the account or user object
       * @param account accountService.get() or TeamMember or Candidate
       * @param ablyKey
       * @returns {*} Socket
       */
      function _create(account, clientKey = 'default') {
        if (svc.account === account && svc.channel) return svc.channel;

        const channelId = _getChannelId(account);
        const clientId = _getClientId(account);
        const isCandidate = _isCandidate(account);

        ablyClientService.connect({
          svc,
          clientKey,
          clientId,
          channelId,
        });

        if (!svc.channel) {
          return;
        }

        svc.account = account;

        try {
          // presence
          svc.channel.presence.enter();

          // create
          _bindToChannel('model.created', (data) => {
            // TODO: only inject or update when we care about the data
            _inject(data, 'created');
          });

          // update
          _bindToChannel('model.updated', (data) => {
            // TODO: only inject or update when we care about the data
            // only patch data if a candidate
            _inject(data, 'updated', isCandidate);
          });

          // digest update (server emits this when an embedded digest is saved)
          // Treat as a standard update so any already-loaded instances refresh and routes/lists react normally.
          _bindToChannel('model.digest_updated', (data) => {
            $log.debug('!!! Digest updated', data);
            _inject(data, 'updated', isCandidate);
          });

          // signal
          if (permissionsService.feature('signals')) {
            _bindToChannel('model.signal', (data) => {
              _inject(data, 'signal');
            });
          }

          // destroy
          _bindToChannel('model.destroyed', (data) => {
            if (data.type && DS.definitions[data.type]) {
              let instance = DS.get(data.type, data.id);

              if (instance) {
                DS.eject(data.type, data.id);
                _broadcast('destroyed', data);
              }
            }
          });

          _bindToChannel('error', ({ type, data }) => {
            $rootScope.$broadcast('socket:error', { data, type });
          });

          _bindToChannel('success', ({ type, data }) => {
            $rootScope.$broadcast('socket:success', { data, type });
          });

          _bindToChannel('test', (data) => {
            $window.console.dir(data);
          });

          if (clientKey === 'default') {
            releaseAlertService.listen(svc);
          }
        } catch (ex) {
          $log.error('Error creating Ably connection', ex);
        }

        // make sure any waiting callbacks are ran
        whenReadyCallbacks.forEach((cb) => cb());
        whenReadyCallbacks.length = 0;

        return svc;
      }

      function _bindToChannel(event, handler) {
        svc.channel.subscribe(event, (msg) => {
          try {
            handler(msg.data);
          } catch (err) {
            $log.error(`Error from socket on event '${event}`, err, msg);
          }
        });
      }

      /**
       * Gets the channel ID for an account or user object
       * @param account
       * @returns {*}
       * @private
       */
      function _getChannelId(account) {
        if (account.user) account = account.user;
        if (account.team) {
          return account.team.envRoomId;
        }
        return account.envRoomId;
      }

      /**
       * Gets the client ID for an account or user object
       * @param account
       * @returns {*}
       * @private
       */
      function _getClientId(account) {
        return account.user ? account.user.id : account.id;
      }

      /**
       * Returns true if the user is a candidate
       * @param account
       * @returns {*}
       * @private
       */
      function _isCandidate(account) {
        return (account.user ? account.user : account).$type === 'Candidate';
      }

      /**
       * Broadcasts the event 6 ways:
       *
       * model.[event]
       * model.changed
       * model.[event]:type
       * model.changed:type
       * model.[event]:type:id (existing only)
       * model.changed:type:id (existing only)
       *
       * @param type
       * @param data
       * @private
       */
      function _broadcast(type, data) {
        $rootScope.$broadcast('socket:model.' + type, data);
        $rootScope.$broadcast('socket:model.changed', data, type);
        $rootScope.$broadcast(`socket:model.${type}:${data.type}`, data);
        $rootScope.$broadcast(`socket:model.changed:${data.type}`, data, type);
        if (type !== 'created' && type !== 'destroyed') {
          $rootScope.$broadcast(
            `socket:model.${type}:${data.type}:${data.id}`,
            data,
          );
          $rootScope.$broadcast(
            `socket:model.changed:${data.type}:${data.id}`,
            data,
            type,
          );
        }
      }

      /**
       * Handles injecting and linking the data.
       * @param data
       * @param eventName
       * @private
       */
      function _inject(data, eventName, allowPatchData) {
        const Model = DS.definitions[data.type];

        if (!Model) {
          return;
        }

        const notify = (eventName, model) => {
          data.model = model;
          _broadcast(eventName, data);
        };

        const meta = Model.meta;
        const instance = Model.get(data.id);

        if (
          instance &&
          data.updatedAt &&
          instance.updatedAt &&
          instance.updatedAt.getTime
        ) {
          // if the updatedAt timestamp is less than that of the data already loaded locally then we can
          // just ignore the inject call.
          data.updatedAt = new Date(data.updatedAt);
          if (instance.updatedAt.getTime() > data.updatedAt.getTime()) {
            // do not bother to call the callback since we will just be ignoring the call
            $log.debug(
              '[Sockets] ignoring old data:',
              data.type,
              data.id,
              `[local: ${instance.updatedAt}]`,
              `[remote: ${data.updatedAt}]`,
            );
            return;
          }
        }

        if (!instance && eventName === 'updated') {
          $log.debug(
            `[Sockets] ignoring ${data.type}(${data.id}) as it's not loaded and this is an 'updated' event`,
            data,
          );
          return;
        }

        // We only inject into instances that are already loaded or that don't have includes configured.
        // If includes are configured then we will want to do a pull so that we can be sure to get all of the data
        // We also only handle if patch data is allowed, which currently is only used for candidate users
        let pushedInstanceProps =
          allowPatchData &&
          instance &&
          angular.isObject(data.model) &&
          Model.deserialize(data.type, data.model);
        if (pushedInstanceProps) {
          // enable the deserializer to clean up the incoming data
          Model.inject(pushedInstanceProps);
          notify(eventName, pushedInstanceProps);
        } else if (meta.neverFetchOnPush) {
          if (instance) {
            // notify listeners that the model changed, but don't reload or update it
            notify(eventName, instance);
          }
        } else if (instance || !meta.fetchOnPush || meta.fetchOnPush(data)) {
          // only look up models if we've already got a copy,
          // or the Model doesn't tell us to ignore pushed data

          // prevent making too many requests at once
          const injectKey = `${data.type}:${data.name}`;
          const alreadyInjecting = injectFlags[injectKey];
          if (alreadyInjecting) {
            // notify that we'll need to make another update
            alreadyInjecting.needsUpdate = true;
            return;
          }

          const options = {
            ignoreLoadingBar: true, // no need to show as busy
            bypassCache: true, // always reload, even if we already have it
          };
          if (meta.includeOnPull) {
            // include anything we always include on socket events
            options.params = {
              includes: meta.includeOnPull.slice(),
            };
          }

          /**
           * Makes a request to the server to load the model
           * @param eventName
           */
          const handleInjection = (eventName) => {
            const injecting = (injectFlags[injectKey] = {});

            Model.find(data.id, options)
              .then((model) => notify(eventName, model))
              .finally(() => {
                // clear flag
                delete injectFlags[injectKey];
                if (injecting.needsUpdate) {
                  handleInjection('updated');
                }
              });
          };

          handleInjection(eventName);
        } else {
          // else ignore the push, don't trigger an event
          $log.debug(
            `[Sockets] ignoring ${data.type}(${data.id}) as it's not loaded`,
            data,
          );
        }
      }
    },
  );
